import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { organisation, type Country, type Location } from '../api/organisation';
import {
  parameters,
  type ActionCompany,
  type CentralTeam,
  type ChecklistTitle,
  type ClosedDay,
  type ClosedDayScope,
  type ScoreBand,
  type ScoreBands,
} from '../api/parameters';
import type { QuestionCategory } from '../api/types';
import { tn } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { BandChip } from '../ui/BandChip';
import { Button } from '../ui/Button';
import { Drawer } from '../ui/Drawer';
import { Alert, Field, Select } from '../ui/Field';
import { ActiveChip, DefinitionTable } from './DefinitionTable';

/** Loads a list and gives back a way to load it again. */
function useRows<T>(load: () => Promise<T[]>, deps: unknown[]) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await load());
    } finally {
      setLoading(false);
    }
  }, deps);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { rows, loading, reload };
}

// ---------------------------------------------------------------- checklist titles

/**
 * The top of the checklist library. A title groups categories, and a category can belong to
 * several titles — the customer's own data has fifteen categories shared across six titles,
 * because İŞ GÜVENLİĞİ means the same thing on a store audit and on a dealer visit.
 */
export function ChecklistTitles() {
  const t = useTranslate();
  const { rows, loading, reload } = useRows(() => parameters.checklistTitles.list(), []);
  const { rows: categories } = useRows(() => api.categories.list(), []);
  const [editing, setEditing] = useState<ChecklistTitle | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function saveCategories() {
    if (!editing || busy) return;
    setBusy(true);
    try {
      await parameters.checklistTitles.update(editing.id, {
        name: editing.name,
        description: editing.description,
        isActive: editing.isActive,
        categoryIds: picked,
      });
      setEditing(null);
      setError(null);
      await reload();
    } catch (e) {
      setError(describeError(e, 'error.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <DefinitionTable<ChecklistTitle, { name: string; description: string; isActive: boolean }>
        title={t('checklistTitles.title')}
        subtitle={t('checklistTitles.subtitle')}
        rows={rows}
        loading={loading}
        blank={{ name: '', description: '', isActive: true }}
        toDraft={(r) => ({
          name: r.name,
          description: r.description ?? '',
          isActive: r.isActive,
        })}
        fields={[
          { key: 'name', label: t('common.name'), placeholder: 'DENETİM - MAĞAZA KONTROL LİSTESİ' },
          { key: 'description', label: t('common.description') },
          { key: 'isActive', label: t('common.status'), kind: 'toggle', tight: true },
        ]}
        columns={[
          {
            label: t('checklistTitles.categories'),
            render: (r) => (
              <button
                className="nv-usage"
                type="button"
                onClick={() => {
                  setEditing(r);
                  setPicked(r.categoryIds);
                }}
              >
                {r.categoryNames.length > 0
                  ? t('checklistTitles.someCategories', {
                      count: r.categoryNames.length,
                      questions: r.questionCount,
                    })
                  : t('checklistTitles.noCategories')}
              </button>
            ),
          },
        ]}
        cell={(row, key) =>
          key === 'isActive' ? (
            <ActiveChip active={row.isActive} />
          ) : key === 'description' ? (
            (row.description ?? <span className="nv-hint">{t('common.none')}</span>)
          ) : (
            row.name
          )
        }
        save={async (d, id) => {
          const body = {
            name: d.name.trim(),
            description: d.description.trim() || null,
            isActive: d.isActive,
            categoryIds: id ? (rows.find((r) => r.id === id)?.categoryIds ?? []) : [],
          };
          if (id) await parameters.checklistTitles.update(id, body);
          else await parameters.checklistTitles.create(body);
          await reload();
        }}
        remove={async (row) => {
          await parameters.checklistTitles.remove(row.id);
          await reload();
        }}
        describe={(row) => ({
          name: row.name,
          // The server refuses a title a template points at; what this can say in advance
          // is how much grouping work would be lost.
          warning: row.categoryNames.length > 0 ? tn('checklistTitles.inUseWarning', row.categoryNames.length) : undefined,
        })}
        empty={t('checklistTitles.empty')}
      />

      <Drawer
        open={editing !== null}
        title={editing?.name ?? ''}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={busy}
              loading={busy}
              loadingText={t('common.saving')}
              onClick={() => void saveCategories()}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <p className="nv-hint">{t('checklistTitles.pickHint')}</p>

        <div className="nv-permission-list">
          {(categories as QuestionCategory[]).map((category) => (
            <label className="nv-permission" key={category.id}>
              <input
                type="checkbox"
                checked={picked.includes(category.id)}
                // From the previous value rather than the one this render closed over:
                // two ticks inside one render would otherwise both start from the same
                // list and the second would throw the first away.
                onChange={() =>
                  setPicked((previous) =>
                    previous.includes(category.id)
                      ? previous.filter((id) => id !== category.id)
                      : [...previous, category.id],
                  )
                }
              />
              <span>
                {category.name}
                <span className="nv-hint">
                  {category.code} · {t('checklistTitles.questions', { count: category.questionCount })}
                </span>
              </span>
            </label>
          ))}
        </div>

        {categories.length === 0 ? <p className="nv-hint">{t('checklistTitles.noLibrary')}</p> : null}

        {error ? <Alert>{error}</Alert> : null}
      </Drawer>
    </>
  );
}

// ---------------------------------------------------------------- score bands

/**
 * The letter beside the number. A band is a lower bound and nothing else, so two of them
 * cannot claim the same score and none of them can leave a gap — the screen shows a range
 * by reading where the next band starts.
 */
export function ScoreBandsScreen() {
  const t = useTranslate();
  const [set, setSet] = useState<ScoreBands>({ bands: [], problems: [] });
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setSet(await parameters.scoreBands.list());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const range = (band: ScoreBand) =>
    band.until === null
      ? t('scoreBands.andAbove', { from: band.from })
      : t('scoreBands.range', { from: band.from, until: band.until });

  return (
    <>
      {set.problems.map((problem) => (
        <Alert tone="warning" key={problem.code}>{t(problem.code, problem.args)}</Alert>
      ))}

      <DefinitionTable<
        ScoreBand,
        { code: string; name: string; from: string; colour: string; isActive: boolean }
      >
        title={t('scoreBands.title')}
        subtitle={t('scoreBands.subtitle')}
        rows={set.bands}
        loading={loading}
        blank={{ code: '', name: '', from: '', colour: '', isActive: true }}
        toDraft={(b) => ({
          code: b.code,
          name: b.name,
          from: String(b.from),
          colour: b.colour ?? '',
          isActive: b.isActive,
        })}
        fields={[
          { key: 'code', label: t('scoreBands.code'), tight: true, placeholder: 'A' },
          { key: 'name', label: t('common.name'), placeholder: 'Çok iyi' },
          { key: 'from', label: t('scoreBands.from'), tight: true, placeholder: '85' },
          { key: 'colour', label: t('scoreBands.colour'), tight: true, placeholder: '#1B7F4A' },
          { key: 'isActive', label: t('common.status'), kind: 'toggle', tight: true },
        ]}
        columns={[{ label: t('scoreBands.covers'), render: range }]}
        cell={(row, key) =>
          key === 'isActive' ? (
            <ActiveChip active={row.isActive} />
          ) : key === 'code' ? (
            <BandChip band={row.code} colour={row.colour} />
          ) : key === 'from' ? (
            <span className="nv-code">{row.from}</span>
          ) : key === 'colour' ? (
            (row.colour ? <span className="nv-code">{row.colour}</span> : <span className="nv-hint">—</span>)
          ) : (
            row.name
          )
        }
        save={async (d, id) => {
          const body = {
            code: d.code.trim(),
            name: d.name.trim(),
            from: Number(d.from.replace(',', '.')),
            colour: d.colour.trim() || null,
            isActive: d.isActive,
          };
          setSet(id ? await parameters.scoreBands.update(id, body) : await parameters.scoreBands.create(body));
        }}
        remove={async (row) => {
          setSet(await parameters.scoreBands.remove(row.id));
        }}
        describe={(row) => ({ name: `${row.code} · ${row.name}`, warning: t('scoreBands.deleteWarning') })}
        empty={t('scoreBands.empty')}
      />
    </>
  );
}

// ---------------------------------------------------------------- action firms

/** Outside firms an action can be handed to — the people who fix the sign, service the tills. */
export function ActionCompanies() {
  const t = useTranslate();
  const { rows, loading, reload } = useRows(() => parameters.actionCompanies.list(), []);

  return (
    <DefinitionTable<
      ActionCompany,
      { name: string; description: string; addresses: string; isActive: boolean }
    >
      title={t('actionCompanies.title')}
      subtitle={t('actionCompanies.subtitle')}
      rows={rows}
      loading={loading}
      blank={{ name: '', description: '', addresses: '', isActive: true }}
      toDraft={(r) => ({
        name: r.name,
        description: r.description ?? '',
        addresses: r.addresses.join(', '),
        isActive: r.isActive,
      })}
      fields={[
        { key: 'name', label: t('common.name') },
        { key: 'description', label: t('common.description') },
        { key: 'addresses', label: t('actionCompanies.addresses'), placeholder: 'a@x.com, b@x.com' },
        { key: 'isActive', label: t('common.status'), kind: 'toggle', tight: true },
      ]}
      cell={(row, key) =>
        key === 'isActive' ? (
          <ActiveChip active={row.isActive} />
        ) : key === 'addresses' ? (
          row.addresses.length > 0 ? (
            <div className="nv-badge-group">
              {row.addresses.map((address) => (
                <Badge key={address} tone="neutral">{address}</Badge>
              ))}
            </div>
          ) : (
            <Badge tone="na">{t('actionCompanies.noAddresses')}</Badge>
          )
        ) : key === 'description' ? (
          (row.description ?? <span className="nv-hint">{t('common.none')}</span>)
        ) : (
          row.name
        )
      }
      save={async (d, id) => {
        const body = {
          name: d.name.trim(),
          description: d.description.trim() || null,
          // Sent as typed. The server splits on colons, semicolons and commas, so a column
          // pasted out of the old system lands as several addresses rather than one.
          addresses: d.addresses.trim() ? [d.addresses] : [],
          isActive: d.isActive,
        };
        if (id) await parameters.actionCompanies.update(id, body);
        else await parameters.actionCompanies.create(body);
        await reload();
      }}
      remove={async (row) => {
        await parameters.actionCompanies.remove(row.id);
        await reload();
      }}
      describe={(row) => ({ name: row.name })}
      empty={t('actionCompanies.empty')}
    />
  );
}

// ---------------------------------------------------------------- central teams

export function CentralTeams() {
  const t = useTranslate();
  const { rows, loading, reload } = useRows(() => parameters.centralTeams.list(), []);

  return (
    <DefinitionTable<CentralTeam, { name: string; description: string; isActive: boolean }>
      title={t('centralTeams.title')}
      subtitle={t('centralTeams.subtitle')}
      rows={rows}
      loading={loading}
      blank={{ name: '', description: '', isActive: true }}
      toDraft={(r) => ({ name: r.name, description: r.description ?? '', isActive: r.isActive })}
      fields={[
        { key: 'name', label: t('common.name') },
        { key: 'description', label: t('common.description') },
        { key: 'isActive', label: t('common.status'), kind: 'toggle', tight: true },
      ]}
      cell={(row, key) =>
        key === 'isActive' ? (
          <ActiveChip active={row.isActive} />
        ) : key === 'description' ? (
          (row.description ?? <span className="nv-hint">{t('common.none')}</span>)
        ) : (
          row.name
        )
      }
      save={async (d, id) => {
        const body = {
          name: d.name.trim(),
          description: d.description.trim() || null,
          isActive: d.isActive,
        };
        if (id) await parameters.centralTeams.update(id, body);
        else await parameters.centralTeams.create(body);
        await reload();
      }}
      remove={async (row) => {
        await parameters.centralTeams.remove(row.id);
        await reload();
      }}
      describe={(row) => ({ name: row.name })}
      empty={t('centralTeams.empty')}
    />
  );
}

// ---------------------------------------------------------------- closed days

/**
 * The two calendars planning reads. A visit scheduled for a day the shop is shut wastes an
 * auditor's day, and an audit that never happened because nobody was there is afterwards
 * indistinguishable from one that was skipped.
 */
export function ClosedDays() {
  const t = useTranslate();
  const [scope, setScope] = useState<ClosedDayScope>('Country');
  const { rows: countries, loading: countriesLoading } = useRows(() => organisation.countries.list(), []);
  const { rows: locations, loading: locationsLoading } = useRows(() => organisation.locations.list(), []);
  const { rows, loading, reload } = useRows(() => parameters.closedDays.list({ scope }), [scope]);

  const targets: { id: string; name: string }[] =
    scope === 'Country'
      ? (countries as Country[]).map((c) => ({ id: c.id, name: c.name }))
      : (locations as Location[]).map((l) => ({ id: l.id, name: `${l.code} · ${l.name}` }));

  return (
    <DefinitionTable<ClosedDay, { scopeId: string; date: string; name: string }>
      title={t('closedDays.title')}
      subtitle={t('closedDays.subtitle')}
      rows={rows}
      loading={loading}
      blank={{ scopeId: targets[0]?.id ?? '', date: '', name: '' }}
      toDraft={(d) => ({ scopeId: d.scopeId, date: d.date, name: d.name })}
      fields={[
        {
          key: 'scopeId',
          label: t(`scope.${scope}`),
          kind: 'select',
          options: targets.map((x) => ({ value: x.id, label: x.name })),
        },
        { key: 'date', label: t('closedDays.date'), tight: true, kind: 'date' },
        { key: 'name', label: t('closedDays.reason'), placeholder: 'Cumhuriyet Bayramı' },
      ]}
      cell={(row, key) =>
        key === 'scopeId' ? (
          row.scopeName
        ) : key === 'date' ? (
          <span className="nv-code">{row.date}</span>
        ) : (
          row.name
        )
      }
      save={async (d, id) => {
        // A date picker hands over an empty string for an impossible day; saying so here beats
        // the server's generic "could not be saved" (field test, 2 November).
        if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date.trim())) throw Object.assign(new Error('date'), { code: 'closed-day.date.required' });
        const body = { scope, scopeId: d.scopeId, date: d.date.trim(), name: d.name.trim() };
        if (id) await parameters.closedDays.update(id, body);
        else await parameters.closedDays.create(body);
        await reload();
      }}
      remove={async (row) => {
        await parameters.closedDays.remove(row.id);
        await reload();
      }}
      describe={(row) => ({ name: `${row.date} · ${row.name}` })}
      empty={t('closedDays.empty')}
      blocked={!(scope === 'Country' ? countriesLoading : locationsLoading) && targets.length === 0 ? t(`closedDays.need${scope}`) : undefined}
    >
      <Field label={t('closedDays.calendar')}>
        {({ id }) => (
          <Select id={id} value={scope} onChange={(e) => setScope(e.target.value as ClosedDayScope)}>
            <option value="Country">{t('closedDays.countryCalendar')}</option>
            <option value="Location">{t('closedDays.locationCalendar')}</option>
          </Select>
        )}
      </Field>
    </DefinitionTable>
  );
}
